import React, { useMemo } from "react";
import { motion } from "framer-motion";
import PageHeader from "../../components/PageHeader";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import moment from "../../helpers/moment";
import is_money from "../../helpers/is_money";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import StartNewApplication from "./StartNewApplication";
import PrintSlip from "./PrintSlip";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import EmptyState from "../../components/ui/EmptyState";
import {
  FiDollarSign,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiHash,
  FiAlertCircle,
} from "react-icons/fi";

const NewApplication = ({ wizardMode = false }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["paymentList"],
    queryFn: async () => {
      const req = await axiosget("ms_application_fee");
      return req?.data;
    },
  });

  // Separate payments into available and used
  const { availablePayments, usedPayments } = useMemo(() => {
    if (!data || data?.type !== undefined) {
      return { availablePayments: [], usedPayments: [] };
    }

    const available = data.filter((v) => v?.application === null && v?.isverified);
    const used = data.filter((v) => v?.application !== null);

    return { availablePayments: available, usedPayments: used };
  }, [data]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  return (
    <>
      {!wizardMode && <PageHeader
        title="New Application"
        subtitle="Apply for multiple courses — only one will be offered admission"
        icon={<FiFileText className="w-5 h-5" />}
      />}

      {isLoading && <Loader />}
      {error && <Alert variant="danger"><AlertDescription>{`Local server error: ${error}`}</AlertDescription></Alert>}
      {data?.type !== undefined && <Alert variant="danger"><AlertDescription>{`${data?.message}`}</AlertDescription></Alert>}

      {!isLoading && data && data?.type === undefined && data.length === 0 && (
        <EmptyState
          variant="no-data"
          title="No Payments Available"
          description="You need to generate and verify a payment before you can start a new application."
          icon={<FiDollarSign className="h-12 w-12" />}
        />
      )}

      {!isLoading && data && data?.type === undefined && (
        <>
          {/* Available Payments Section */}
          {availablePayments.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-1 flex-1 bg-gradient-to-r from-green-200 to-emerald-200 rounded" />
                <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                  <FiCheckCircle className="h-5 w-5 text-green-600" />
                  Available Payments ({availablePayments.length})
                </h2>
                <div className="h-1 flex-1 bg-gradient-to-l from-green-200 to-emerald-200 rounded" />
              </div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                {availablePayments.map((v, i) => (
                  <motion.div key={`_available_payment_${i}`} variants={cardVariants}>
                    <Card
                      variant="success"
                      className="hover:shadow-lg transition-all duration-300 h-full border-2 border-green-200"
                    >
                      <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <CardTitle className="flex items-center gap-2">
                            <FiDollarSign className="h-5 w-5 text-green-600" />
                            Available Payment
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge variant="success" dot>
                              Verified
                            </Badge>
                            <Badge variant="primary" size="sm">
                              Ready to Use
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="pt-5 space-y-4">
                        {/* Reference Code */}
                        <div className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-200">
                          <div className="flex items-center gap-2 mb-2">
                            <FiHash className="h-4 w-4 text-primary-600" />
                            <p className="text-xs font-medium text-primary-900">
                              {v?.trxdata?.gateway ? v.trxdata.gatewayDisplayName || "Payment Reference" : "Remita RRR"}
                            </p>
                          </div>
                          <p className="text-lg font-bold text-primary-700 font-mono">
                            {v?.trxcode}
                          </p>
                        </div>

                        {/* Amount */}
                        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                          <span className="text-sm font-medium text-neutral-700">Amount</span>
                          <span className="text-xl font-bold text-neutral-900">
                            {is_money(v?.amount)}
                          </span>
                        </div>

                        {/* Description */}
                        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <FiFileText className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-blue-900 mb-1">Description</p>
                            <p className="text-sm text-blue-700">
                              {v?.trxdata?.description || "Application Fee Payment"}
                            </p>
                          </div>
                        </div>
                      </CardContent>

                      <CardFooter className="flex-col items-stretch gap-3 bg-green-50 border-t border-green-100">
                        <div className="flex items-center justify-center">
                          <StartNewApplication v={v} />
                        </div>
                        <p className="text-xs text-neutral-500 text-center">
                          Generated {moment(v.createdAt)} ago
                        </p>
                      </CardFooter>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          )}

          {/* Used Payments Section */}
          {usedPayments.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-1 flex-1 bg-gradient-to-r from-neutral-200 to-gray-200 rounded" />
                <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                  <FiAlertCircle className="h-5 w-5 text-neutral-600" />
                  Used Payments ({usedPayments.length})
                </h2>
                <div className="h-1 flex-1 bg-gradient-to-l from-neutral-200 to-gray-200 rounded" />
              </div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                {usedPayments.map((v, i) => (
                  <motion.div key={`_used_payment_${i}`} variants={cardVariants}>
                    <Card
                      variant="default"
                      className="hover:shadow-lg transition-all duration-300 h-full opacity-90"
                    >
                      <CardHeader className="bg-gradient-to-r from-neutral-50 to-gray-50 border-b border-neutral-100">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <CardTitle className="flex items-center gap-2">
                            <FiDollarSign className="h-5 w-5 text-neutral-600" />
                            Used Payment
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge variant="info" dot>
                              Application Created
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="pt-5 space-y-4">
                        {/* Reference Code */}
                        <div className="p-4 bg-gradient-to-r from-neutral-50 to-gray-50 rounded-lg border border-neutral-200">
                          <div className="flex items-center gap-2 mb-2">
                            <FiHash className="h-4 w-4 text-neutral-600" />
                            <p className="text-xs font-medium text-neutral-900">
                              {v?.trxdata?.gateway ? v.trxdata.gatewayDisplayName || "Payment Reference" : "Remita RRR"}
                            </p>
                          </div>
                          <p className="text-lg font-bold text-neutral-700 font-mono">
                            {v?.trxcode}
                          </p>
                        </div>

                        {/* Amount */}
                        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                          <span className="text-sm font-medium text-neutral-700">Amount</span>
                          <span className="text-xl font-bold text-neutral-900">
                            {is_money(v?.amount)}
                          </span>
                        </div>

                        {/* Description */}
                        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <FiFileText className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-blue-900 mb-1">Description</p>
                            <p className="text-sm text-blue-700">
                              {v?.trxdata?.description || "Application Fee Payment"}
                            </p>
                          </div>
                        </div>

                        {/* Application Info */}
                        <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                          <FiCheckCircle className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-purple-900 mb-1">
                              Used For Application
                            </p>
                            <p className="text-sm text-purple-700 font-semibold">
                              {v?.application || "Application Created"}
                            </p>
                          </div>
                        </div>
                      </CardContent>

                      <CardFooter className="flex-col items-stretch gap-3 bg-neutral-50 border-t border-neutral-100">
                        <div className="flex items-center justify-center">
                          <PrintSlip v={v} key={`print_slip_key_${i}`} />
                        </div>
                        <p className="text-xs text-neutral-500 text-center">
                          Generated {moment(v.createdAt)} ago
                        </p>
                      </CardFooter>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          )}

          {/* No verified payments available */}
          {availablePayments.length === 0 && usedPayments.length === 0 && data.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <FiAlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-amber-900 mb-2">
                    No Verified Payments Available
                  </h3>
                  <p className="text-sm text-amber-800">
                    All your payments are either pending verification or have already been used for applications.
                    Please verify your existing payments or generate a new RRR to start a new application.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default NewApplication;
